import {
  DataTableSource,
  type DataTablePageReq,
  type DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import { listTrainingScheduleOccurrences } from "@/api/trainingSchedule";
import { ScheduleOccurrenceTableRow } from "./ScheduleOccurrenceTableRow";

// 한 번에 받는 상한. 상류 `limit` 의 최대값(1..500)이다. offset 이 없어서 이보다 오래된 실행은
// 창이 표시하지 못한다. 캡션이 그 사실을 알린다.
export const MAX_OCCURRENCE_LIMIT = 500;

export const OCCURRENCE_PAGE_SIZE = 50;

type ScheduleOccurrenceTableSourceOptions = {
  scheduleId: string;
  resolveErrorMessage: (error: unknown) => string;
};

/**
 * 실행 이력 표 source. 상류는 최신순 `limit` 만 받고 offset 이 없다. 그래서 상한(500)까지 한 번에
 * 받고 페이지는 클라이언트에서 자른다.
 *   - 받은 목록은 인스턴스가 보관한다. 그래서 페이지 이동에는 요청이 없고 `reload()` 만 다시 받는다.
 *   - 건너뜀 필터도 받은 목록을 자를 뿐 다시 받지 않는다.
 *   - 창 콘텐츠가 소유(생성·dispose)하므로 최소화해도 목록이 남는다.
 */
export class ScheduleOccurrenceTableSource extends DataTableSource<ScheduleOccurrenceTableRow> {
  private readonly scheduleId: string;
  private all: readonly ScheduleOccurrenceTableRow[] | null = null;
  // 건너뜀(skipped) 회차를 표시할지. 기본은 숨긴다. 정상 회차지만 한 일이 없어 볼 일이 드물다.
  private includeSkipped = false;
  // 받는 중인 요청. 페이지 이동과 새로고침이 겹쳐도 같은 목록을 두 번 받지 않는다.
  private inflight: Promise<readonly ScheduleOccurrenceTableRow[]> | null = null;

  constructor({ scheduleId, resolveErrorMessage }: ScheduleOccurrenceTableSourceOptions) {
    super({ initialPageSize: OCCURRENCE_PAGE_SIZE, resolveErrorMessage });
    this.scheduleId = scheduleId;
  }

  // 새로고침. 받은 목록을 버리고 현재 페이지를 다시 받는다. 건너뜀 필터는 그대로 둔다.
  reload(): Promise<void> {
    this.all = null;
    this.inflight = null;
    return this.refresh();
  }

  /**
   * 창이 닫히면 받은 목록과 진행 중 요청을 함께 버린다(rules §12).
   *
   * 베이스는 `disposed` 와 요청 세대를 올려 자기 page 요청을 무효화한다. 그러나 이 source 가 따로
   * 가진 상한 500행 목록과 `inflight` 는 모른다. `inflight` 를 비워야 dispose 전에 보낸 요청의
   * 응답이 돌아와도 `this.inflight === request` 검사에서 걸려 `all` 을 다시 채우지 못한다. 베이스의
   * `subscribe` 는 dispose 뒤 다시 활성화될 수 있는 진입점을 열어 둔다. 그래서 남은 목록은 다음
   * 수명주기로 의도하지 않게 이어진다.
   */
  override dispose(): void {
    this.all = null;
    this.inflight = null;
    super.dispose();
  }

  /**
   * 최소화 복원. 이미 받은 목록이 있으면 다시 만들지 않는다.
   *
   * 패널은 최소화할 때 언마운트되고 복원할 때 `initialize()` 를 다시 호출한다. 베이스
   * `initialize()` 는 `refresh()` 라서 스냅샷을 loading 으로 바꾼다. 목록은 인스턴스가 보관하므로
   * 왕복은 없지만 "실행 이력을 불러오고 있습니다." 가 한 프레임 보인다.
   */
  override initialize(): Promise<void> {
    if (this.all !== null && this.getSnapshot().loadState === "loaded") {
      return Promise.resolve();
    }
    return super.initialize();
  }

  // 상류에서 받은 수(필터 전). 상한 캡션과 "모두 걸러져 비었다" 판정은 보이는 수가 아니라 이 수로 한다.
  get fetchedCount(): number {
    return this.all?.length ?? 0;
  }

  // 필터가 바뀌면 페이지 수가 달라지므로 1페이지로 간다. 받은 목록을 다시 자를 뿐 요청하지 않는다.
  setIncludeSkipped(value: boolean): Promise<void> {
    if (value === this.includeSkipped) {
      return Promise.resolve();
    }
    this.includeSkipped = value;
    return this.goToPage(1);
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<ScheduleOccurrenceTableRow>> {
    const all = this.all ?? (await this.fetchAll());
    const visible = this.includeSkipped
      ? all
      : all.filter((row) => row.occurrence.status !== "skipped");
    const page = Math.max(1, Math.min(req.page, Math.ceil(visible.length / req.size)));
    const start = (page - 1) * req.size;
    return {
      rows: visible.slice(start, start + req.size),
      total: visible.length,
      page,
      size: req.size,
    };
  }

  private fetchAll(): Promise<readonly ScheduleOccurrenceTableRow[]> {
    if (this.inflight === null) {
      const request = listTrainingScheduleOccurrences(this.scheduleId, MAX_OCCURRENCE_LIMIT).then(
        (occurrences) => {
          const rows = occurrences.map((occurrence) => new ScheduleOccurrenceTableRow(occurrence));
          // 그 사이 reload 가 이 요청을 버렸으면(inflight 교체) 오래된 응답으로 목록을 덮지 않는다.
          if (this.inflight === request) {
            this.all = rows;
            this.inflight = null;
          }
          return rows;
        },
        (error: unknown) => {
          if (this.inflight === request) {
            this.inflight = null;
          }
          throw error;
        },
      );
      this.inflight = request;
    }
    return this.inflight;
  }
}
